import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import styles from './EventsPage.module.css';
import { FaMapMarkerAlt, FaUserFriends, FaCalendarAlt, FaEdit, FaUsers, FaCheckCircle, FaTimesCircle, FaQuestionCircle } from 'react-icons/fa';
import { GoogleMap, Marker, useLoadScript } from '@react-google-maps/api';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import axios from 'axios';
import { Link } from 'react-router-dom';
import api from '../api/api';
import EventModal from '../components/EventModal'; 
import RSVPButtons from '../components/RSVPButtons';
import EventCard from '../components/EventCard';
import { ResponsiveContainer, PieChart, Pie, Tooltip, Legend, Cell } from 'recharts';
import { useRSVP } from '../hooks/useRSVP';
import { useEventSSE, SSEConnectionStatus } from '../hooks/useSSE';
import { useUser } from '../context/UserContext';
import { canManageProgram } from '../hooks/useProgramsApi';

const EventsPage = () => {
  const { clubId, programId } = useParams();
  const location = useLocation();
  const { userClubs, userProfile } = useUser();
  
  // Get eventId from URL query parameters
  const urlParams = new URLSearchParams(location.search);
  const highlightEventId = urlParams.get('eventId');
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [highlightedDates, setHighlightedDates] = useState([]);
  const [eventsByDate, setEventsByDate] = useState({}); // Map dates to events for status-based coloring
  const [weatherData, setWeatherData] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null); // For editing events
  
  // Filtering and categorization state
  const [activeTab, setActiveTab] = useState('upcoming'); // 'upcoming' or 'past'
  const [statusFilter, setStatusFilter] = useState('active-live-planned'); // Show Active, Live & Planned by default
  
  // Summary section state
  const [isSummaryExpanded, setIsSummaryExpanded] = useState(false);
  
  // Weekly grouping state - track which weeks are expanded
  const [expandedWeeks, setExpandedWeeks] = useState({});

  // Program permissions state
  const [programPermissions, setProgramPermissions] = useState([]);
  const [permissionsLoading, setPermissionsLoading] = useState(false);
  const [resolvedClubId, setResolvedClubId] = useState(clubId);
  const [programDetails, setProgramDetails] = useState(null);

  // Fetch weather function
  // Weather cache to prevent excessive API calls
  const weatherCacheRef = React.useRef({});
  const WEATHER_CACHE_DURATION = 15 * 60 * 1000; // 15 minutes

  const fetchWeather = async (cityName, eventId) => {
    try {
      // Check cache first
      const cacheKey = `${cityName}_${eventId}`;
      const cached = weatherCacheRef.current[cacheKey];
      
      if (cached && (Date.now() - cached.timestamp) < WEATHER_CACHE_DURATION) {
        console.log(`☁️ Using cached weather for ${cityName}`);
        setWeatherData(prevWeather => ({
          ...prevWeather,
          [eventId]: cached.data,
        }));
        return;
      }

      // Fetch from API only if cache is stale
      console.log(`🌤️ Fetching fresh weather for ${cityName}`);
      const apiKey = process.env.REACT_APP_OPENWEATHER_API_KEY;
      const wURL = `https://api.openweathermap.org/data/2.5/weather?q=${cityName}&appid=${apiKey}&units=metric`;
      const response = await axios.get(wURL);
      
      // Update cache
      weatherCacheRef.current[cacheKey] = {
        data: response.data,
        timestamp: Date.now()
      };
      
      setWeatherData(prevWeather => ({
        ...prevWeather,
        [eventId]: response.data,
      }));
    } catch (error) {
      console.error('Error fetching weather data:', error);
      // If rate limited, try to use old cache
      const cacheKey = `${cityName}_${eventId}`;
      const cached = weatherCacheRef.current[cacheKey];
      if (cached) {
        console.log(`⚠️ Rate limited, using stale cache for ${cityName}`);
        setWeatherData(prevWeather => ({
          ...prevWeather,
          [eventId]: cached.data,
        }));
      }
    }
  };

  // Lightweight refresh - just event data, no weather
  const refreshEventsOnly = useCallback(async () => {
    try {
      console.log('🔄 Refreshing events (no weather fetch)');
      const data = await api.get(`/api/events/${programId}`);
      const eventsArray = Array.isArray(data) ? data : [];
      setEvents(eventsArray);

      // Extract and normalize event dates with status
      const eventDates = eventsArray.map(event => {
        const eventDate = new Date(event.dateTime.start);
        return eventDate.toISOString().split('T')[0];
      });
      setHighlightedDates(eventDates);
      
      // Create map of dates to events for status-based coloring
      const dateMap = {};
      eventsArray.forEach(event => {
        const dateKey = new Date(event.dateTime.start).toISOString().split('T')[0];
        if (!dateMap[dateKey]) {
          dateMap[dateKey] = [];
        }
        dateMap[dateKey].push(event);
      });
      setEventsByDate(dateMap);
      
      // DON'T fetch weather on refresh - use cache
      console.log('✅ Events refreshed (weather preserved from cache)');
    } catch (error) {
      console.error('Error refreshing events:', error);
    }
  }, [programId]);

  // Fetch events function (with weather) - used on initial load
  const fetchEvents = useCallback(async () => {
    try {
      const data = await api.get(`/api/events/${programId}`);
      const eventsArray = Array.isArray(data) ? data : [];
      setEvents(eventsArray);

      // Extract and normalize event dates with status
      const eventDates = eventsArray.map(event => {
        const eventDate = new Date(event.dateTime.start);
        return eventDate.toISOString().split('T')[0];
      });
      setHighlightedDates(eventDates);
      
      // Create map of dates to events for status-based coloring
      const dateMap = {};
      eventsArray.forEach(event => {
        const dateKey = new Date(event.dateTime.start).toISOString().split('T')[0];
        if (!dateMap[dateKey]) {
          dateMap[dateKey] = [];
        }
        dateMap[dateKey].push(event);
      });
      setEventsByDate(dateMap);

      // Fetch weather data for each event by city name (cached for 15 min)
      eventsArray.forEach(event => {
        const city = event.location?.address?.city;
        if (city) {
          fetchWeather(city, event._id);
        }
      });
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  }, [programId]);

  // Authorization logic with proper program-specific validation
  const canManage = useMemo(() => {
    // 1. Check if user has club management role for THIS specific club
    const hasClubRole = Array.isArray(userClubs) && userClubs.some((m) => {
      const membershipClubId = m.club?._id || m.club;
      const role = (m.role || '').toLowerCase();
      const isTargetClub = String(membershipClubId) === String(resolvedClubId);
      const isOwnerOrManager = ['owner', 'creator', 'clubowner', 'clubmanager', 'manager', 'admin'].includes(role);
      return isTargetClub && isOwnerOrManager;
    });

    if (hasClubRole) {
      return true;
    }

    // 2. Check program-specific permissions from backend
    const hasProgramPermissions = canManageProgram(programPermissions);

    if (!hasProgramPermissions) {
      return false;
    }

    // 3. CRITICAL: Even if backend says they have permissions, verify they're not just a regular member
    // Check membership in THIS SPECIFIC PROGRAM (not globally)
    const membershipInThisProgram = Array.isArray(userClubs) && userClubs.find((m) => {
      const membershipClubId = m.club?._id || m.club;
      return String(membershipClubId) === String(resolvedClubId);
    });

    if (membershipInThisProgram) {
      const role = (membershipInThisProgram.role || '').toLowerCase();
      const isOnlyMember = ['member', 'player', 'observer'].includes(role);
      
      if (isOnlyMember) {
        return false;
      }
    }

    return true;
  }, [userClubs, resolvedClubId, programPermissions, programId]);

  // Auto-scroll to highlighted event when page loads
  useEffect(() => {
    if (highlightEventId && events.length > 0) {
      const targetEvent = events.find(e => e._id === highlightEventId);
      if (targetEvent) {
        // Switch to the correct tab based on the event's date
        const now = new Date();
        const eventDate = new Date(targetEvent.dateTime.start);
        const targetTab = eventDate > now ? 'upcoming' : 'past';
        setActiveTab(targetTab);
        // Reset filter to 'all' to ensure the event is visible regardless of its status
        setStatusFilter('all');
      }

      // Small delay to ensure the component is fully rendered after tab/filter change
      const timer = setTimeout(() => {
        const eventElement = document.querySelector(`[data-event-id="${highlightEventId}"]`);
        if (eventElement) {
          eventElement.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center',
            inline: 'nearest'
          });
        }
      }, 600);
      
      return () => clearTimeout(timer);
    }
  }, [highlightEventId, events]);

  // Fetch program details to get clubId if not available in route params
  useEffect(() => {
    if (!clubId && programId) {
      const fetchProgramDetails = async () => {
        try {
          const data = await api.get(`/api/programs/${programId}`);
          const program = data.program || data;
          
          setProgramDetails(program);
          
          const extractedClubId = program.clubId && typeof program.clubId === 'object' 
            ? program.clubId._id 
            : program.clubId;
          setResolvedClubId(extractedClubId);
        } catch (error) {
          console.error('Error fetching program details:', error);
          
          if (Array.isArray(userClubs) && userClubs.length > 0) {
            const managerClub = userClubs.find(m => {
              const role = (m.role || '').toLowerCase();
              return ['owner', 'creator', 'clubowner', 'clubmanager', 'manager', 'admin'].includes(role);
            });
            
            if (managerClub) {
              const fallbackClubId = managerClub.club?._id || managerClub.club;
              setResolvedClubId(fallbackClubId);
            }
          }
        }
      };
      
      fetchProgramDetails();
    } else if (clubId) {
      setResolvedClubId(clubId);
      // Still fetch program details even if we have clubId
      if (programId) {
        const fetchProgramDetailsWithClub = async () => {
          try {
            const data = await api.get(`/api/programs/${programId}`);
            const program = data.program || data;
            setProgramDetails(program);
          } catch (error) {
            console.error('Error fetching program details:', error);
          }
        };
        fetchProgramDetailsWithClub();
      }
    } else {
      // No clubId available and no programId to fetch from
    }
  }, [clubId, programId, userClubs]);

  // Log when resolvedClubId changes
  useEffect(() => {
    // Temporary fallback: if still no clubId after 3 seconds, use the first club where user is a manager
    if (!resolvedClubId && userClubs && userClubs.length > 0) {
      const timer = setTimeout(() => {
        const managerClub = userClubs.find(m => {
          const role = (m.role || '').toLowerCase();
          return ['owner', 'creator', 'clubowner', 'clubmanager', 'manager', 'admin'].includes(role);
        });
        
        if (managerClub) {
          const fallbackClubId = managerClub.club?._id || managerClub.club;
          setResolvedClubId(fallbackClubId);
        }
      }, 3000);
      
      return () => clearTimeout(timer);
    }
  }, [resolvedClubId, userClubs]);

  // Fetch program permissions for current user
  useEffect(() => {
    if (!programId) return;
    
    const fetchProgramPermissions = async () => {
      try {
        setPermissionsLoading(true);
        const data = await api.get(`/api/programs/${programId}/permissions`);
        setProgramPermissions(data?.permissions || []);
      } catch (error) {
        console.log('Permissions endpoint not available, using club role only');
        setProgramPermissions([]);
      } finally {
        setPermissionsLoading(false);
      }
    };

    fetchProgramPermissions();
  }, [programId]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents, programId, resolvedClubId, userClubs]);

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
  });

  // Note: Real-time updates are now handled via SSE (Server-Sent Events) at the EventCard level
  // Each EventCard subscribes to its own event-specific SSE stream, which is much more efficient
  // than the previous WebSocket approach that caused connection exhaustion issues.
  // SSE connections are lightweight, auto-reconnect, and work seamlessly with serverless backends.

  // Enhanced calendar tile styling based on event status
  const tileClassName = ({ date }) => {
    const normalizedDate = date.toISOString().split('T')[0];
    const eventsOnDate = eventsByDate[normalizedDate];
    
    if (!eventsOnDate || eventsOnDate.length === 0) return null;
    
    // Determine dominant status for the date
    const statusPriority = { 'Live': 5, 'Active': 4, 'Planned': 3, 'Completed': 2, 'Cancelled': 1 };
    const dominantEvent = eventsOnDate.reduce((prev, curr) => {
      return (statusPriority[curr.status] || 0) > (statusPriority[prev.status] || 0) ? curr : prev;
    });
    
    return `${styles.highlightedDate} ${styles[`calendar${dominantEvent.status}`]}`;
  };
  
  // Show event count badge on dates with events
  const tileContent = ({ date, view }) => {
    if (view !== 'month') return null;
    
    const normalizedDate = date.toISOString().split('T')[0];
    const eventsOnDate = eventsByDate[normalizedDate];
    
    if (!eventsOnDate || eventsOnDate.length === 0) return null;
    
    // Show count badge for multiple events
    if (eventsOnDate.length > 1) {
      return (
        <span className={styles.eventCountBadge}>
          {eventsOnDate.length}
        </span>
      );
    }
    
    // Show dot for single event
    return <span className={styles.eventDot} />;
  };

  // Handle calendar date click - scroll to week
  const handleDateClick = (value) => {
    const clickedDate = new Date(value);
    const weekInfo = getWeekInfo(clickedDate);
    
    // Check if there are events in this week
    const weekEvents = sortedFilteredEvents.filter(event => {
      const eventDate = new Date(event.dateTime.start);
      return eventDate >= weekInfo.monday && eventDate <= weekInfo.sunday;
    });
    
    if (weekEvents.length === 0) {
      console.log('No events in this week');
      return; // Don't scroll if no events
    }
    
    // Ensure the week is expanded
    setExpandedWeeks(prev => ({
      ...prev,
      [weekInfo.weekKey]: true
    }));
    
    // Small delay to allow expansion animation before scrolling
    setTimeout(() => {
      // Find the week section element and scroll to it
      const weekElement = document.querySelector(`[data-week-key="${weekInfo.weekKey}"]`);
      if (weekElement) {
        weekElement.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start',
          inline: 'nearest'
        });
        
        // Add a brief highlight animation
        weekElement.classList.add(styles.weekHighlight);
        setTimeout(() => {
          weekElement.classList.remove(styles.weekHighlight);
        }, 2000);
      }
    }, 100);
  };

  const openModal = (event = null) => {
    // Only allow opening modal if user has management permissions
    if (!canManage) {
      alert('You need management permissions to create or edit events.');
      return;
    }
    setSelectedEvent(event);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedEvent(null);
  };

  // Categorize events by time (upcoming vs past)
  const now = new Date();
  const upcomingEvents = events.filter(event => new Date(event.dateTime.start) > now);
  const pastEvents = events.filter(event => new Date(event.dateTime.start) <= now);
  
  // Apply status filter to current tab's events
  const getCurrentTabEvents = () => {
    const tabEvents = activeTab === 'upcoming' ? upcomingEvents : pastEvents;
    if (statusFilter === 'all') return tabEvents;
    if (statusFilter === 'active-live-planned') {
      return tabEvents.filter(event => 
        event.status === 'Active' || event.status === 'Live' || event.status === 'Planned'
      );
    }
    if (statusFilter === 'active-planned') {
      return tabEvents.filter(event => 
        event.status === 'Active' || event.status === 'Planned'
      );
    }
    return tabEvents.filter(event => event.status.toLowerCase() === statusFilter.toLowerCase());
  };
  
  // Sort filtered events (descending order - newest first)
  const sortedFilteredEvents = getCurrentTabEvents().sort((a, b) => {
    const dateA = new Date(a.dateTime.start);
    const dateB = new Date(b.dateTime.start);
    return dateB - dateA; // Descending - newest first
  });
  
  // Helper function to get week key and date range
  const getWeekInfo = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Adjust to Monday
    const monday = new Date(d.setDate(diff));
    monday.setHours(0, 0, 0, 0);
    
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    sunday.setHours(23, 59, 59, 999);
    
    const weekKey = `${monday.getFullYear()}-W${Math.ceil((monday.getDate() + 6) / 7)}`;
    
    return {
      weekKey,
      monday,
      sunday,
      label: `${monday.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${sunday.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
    };
  };
  
  // Group events by week
  const groupEventsByWeek = (events) => {
    const grouped = {};
    
    events.forEach(event => {
      const eventDate = new Date(event.dateTime.start);
      const weekInfo = getWeekInfo(eventDate);
      
      if (!grouped[weekInfo.weekKey]) {
        grouped[weekInfo.weekKey] = {
          weekKey: weekInfo.weekKey,
          label: weekInfo.label,
          monday: weekInfo.monday,
          sunday: weekInfo.sunday,
          events: []
        };
      }
      
      grouped[weekInfo.weekKey].events.push(event);
    });
    
    // Convert to array and sort by week start date (descending - newest first)
    return Object.values(grouped).sort((a, b) => b.monday - a.monday);
  };
  
  const weeklyGroupedEvents = groupEventsByWeek(sortedFilteredEvents);
  
  // Stable reference for week keys to avoid dependency array issues
  const weekKeysString = useMemo(() => 
    weeklyGroupedEvents.map(w => w.weekKey).join(','), 
    [weeklyGroupedEvents]
  );
  
  // Initialize all weeks as expanded by default
  React.useEffect(() => {
    const initialExpanded = {};
    weeklyGroupedEvents.forEach(week => {
      if (expandedWeeks[week.weekKey] === undefined) {
        initialExpanded[week.weekKey] = true; // Default to expanded
      }
    });
    if (Object.keys(initialExpanded).length > 0) {
      setExpandedWeeks(prev => ({ ...prev, ...initialExpanded }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekKeysString]);
  
  const toggleWeek = (weekKey) => {
    setExpandedWeeks(prev => ({
      ...prev,
      [weekKey]: !prev[weekKey]
    }));
  };
  
  // Get status counts for current tab
  const getStatusCounts = (eventsList) => {
    const counts = eventsList.reduce((acc, event) => {
      const status = event.status;
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {});
    return counts;
  };
  
  const currentTabStatusCounts = getStatusCounts(activeTab === 'upcoming' ? upcomingEvents : pastEvents);

  // KPI and Pie chart data with better colors matching status
  const statusCounts = events.reduce((acc, event) => {
    acc[event.status] = (acc[event.status] || 0) + 1;
    return acc;
  }, {});
  
  const statusColorMap = {
    'Active': '#10b981',
    'Live': '#ffc107', 
    'Planned': '#3b82f6',
    'Completed': '#059669',
    'Cancelled': '#ef4444',
    'Postponed': '#f59e0b'
  };
  
  const pieData = Object.entries(statusCounts).map(([status, value]) => ({ 
    name: status, 
    value,
    color: statusColorMap[status] || '#6c757d'
  }));
  
  // Calculate better KPIs
  const totalAttendance = events.reduce((sum, event) => sum + (event.metrics?.actualAttendees || 0), 0);
  const avgAttendance = events.length > 0 ? Math.round(totalAttendance / events.length) : 0;
  const currentDate = new Date();
  const thisWeekStart = new Date(currentDate.setDate(currentDate.getDate() - currentDate.getDay()));
  const thisWeekEnd = new Date(thisWeekStart);
  thisWeekEnd.setDate(thisWeekEnd.getDate() + 7);
  const eventsThisWeek = upcomingEvents.filter(e => {
    const eventDate = new Date(e.dateTime.start);
    return eventDate >= thisWeekStart && eventDate < thisWeekEnd;
  }).length;
  const completionRate = events.length > 0 ? Math.round(((statusCounts['Completed'] || 0) / events.length) * 100) : 0;

  // Get all distinct locations
  const uniqueLocations = Array.from(new Set(events.map(ev => ev.location?.name).filter(Boolean)));

  // Find the next upcoming Active, Live, or Planned event for ticker
  const nextActiveEvent = upcomingEvents
    .filter(event => event.status === 'Active' || event.status === 'Live' || event.status === 'Planned')
    .sort((a, b) => new Date(a.dateTime.start) - new Date(b.dateTime.start))[0];
  
  // Generate context-aware ticker message
  const getTickerMessage = () => {
    if (nextActiveEvent) {
      const prefix = nextActiveEvent.status === 'Live' ? '🔴 LIVE NOW: ' : '📅 Next: ';
      const eventDate = new Date(nextActiveEvent.dateTime.start);
      const dateStr = eventDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
      const timeStr = eventDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
      return `${prefix}${nextActiveEvent.title} • ${nextActiveEvent.location?.name || 'TBD'} • ${dateStr} at ${timeStr}`;
    }
    // More helpful message when no upcoming events
    if (events.length > 0) {
      return `📊 ${events.length} total events • ${pastEvents.length} completed • Check the Past tab for history`;
    }
    return '📅 No events scheduled yet. Create your first event!';
  };
  
  const nextEventMsg = getTickerMessage();

  if (loading) {
    return <div>Loading events...</div>;
  }

  // Show loading state while permissions are being fetched
  if (permissionsLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.mainContent}>
          <div>Loading permissions...</div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.mainContent}>
        {/* Compact Sticky Header */}
        <div className={styles.compactHeader}>
          {/* Event Category Tabs */}
          <div className={styles.compactTabs}>
            <button
              className={`${styles.compactTabButton} ${activeTab === 'upcoming' ? styles.activeTab : ''}`}
              onClick={() => {
                setActiveTab('upcoming');
                setStatusFilter('active-live-planned'); // Reset to show Active, Live & Planned
              }}
            >
              <span className={styles.tabIcon}>📅</span>
              <span className={styles.tabText}>Upcoming</span>
              <span className={styles.tabCount}>({upcomingEvents.length})</span>
            </button>
            <button
              className={`${styles.compactTabButton} ${activeTab === 'past' ? styles.activeTab : ''}`}
              onClick={() => {
                setActiveTab('past');
                setStatusFilter('all'); // Show all past events
              }}
            >
              <span className={styles.tabIcon}>📜</span>
              <span className={styles.tabText}>Past</span>
              <span className={styles.tabCount}>({pastEvents.length})</span>
            </button>
          </div>

          {/* Status Filters - Compact */}
          <div className={styles.compactFilters}>
            {activeTab === 'upcoming' && (
              <button
                className={`${styles.compactFilterChip} ${statusFilter === 'active-live-planned' ? styles.activeFilter : ''}`}
                onClick={() => setStatusFilter('active-live-planned')}
              >
                Active/Live/Planned <span className={styles.filterCount}>({(currentTabStatusCounts['Active'] || 0) + (currentTabStatusCounts['Live'] || 0) + (currentTabStatusCounts['Planned'] || 0)})</span>
              </button>
            )}
            <button
              className={`${styles.compactFilterChip} ${statusFilter === 'all' ? styles.activeFilter : ''}`}
              onClick={() => setStatusFilter('all')}
            >
              All <span className={styles.filterCount}>({(activeTab === 'upcoming' ? upcomingEvents : pastEvents).length})</span>
            </button>
            {['Active', 'Live', 'Planned', 'Completed', 'Cancelled'].map(status => {
              const count = currentTabStatusCounts[status] || 0;
              return (
                <button
                  key={status}
                  className={`${styles.compactFilterChip} ${styles[status.toLowerCase()]} ${statusFilter === status ? styles.activeFilter : ''}`}
                  onClick={() => setStatusFilter(status)}
                  disabled={count === 0}
                >
                  {status} <span className={styles.filterCount}>({count})</span>
                </button>
              );
            })}
          </div>
          
          {/* Summary Icon Button */}
          <button 
            className={styles.summaryIconButton}
            onClick={() => setIsSummaryExpanded(!isSummaryExpanded)}
            aria-expanded={isSummaryExpanded}
            title="Toggle Summary"
          >
            📊
          </button>
        </div>
        
        {/* News Ticker - Thin Bar Below Header */}
        <div className={styles.thinNewsTickerWrapper}>
          <div className={styles.thinNewsTicker}>
            <span>{nextEventMsg}</span>
          </div>
        </div>
        
        {/* Collapsible Summary Section - No Toggle Button, Controlled by Header Icon */}
        {isSummaryExpanded && (
          <div className={styles.summarySection}>
            <div className={styles.summaryContent}>
            <div className={styles.calendarKpiWrapper}>
              <div className={styles.calendarCard}>
                <h2 className={styles.kpiTitle}>📅 Event Calendar</h2>
                <p className={styles.calendarHint}>💡 Click on a date to jump to that week's events</p>
                <div className={styles.calendarContainer}>
                  <Calendar 
                    tileClassName={tileClassName}
                    tileContent={tileContent}
                    onClickDay={handleDateClick}
                  />
                </div>
                <div className={styles.calendarLegend}>
                  <span className={styles.legendItem}><span className={`${styles.legendDot} ${styles.legendActive}`}></span> Active</span>
                  <span className={styles.legendItem}><span className={`${styles.legendDot} ${styles.legendLive}`}></span> Live</span>
                  <span className={styles.legendItem}><span className={`${styles.legendDot} ${styles.legendPlanned}`}></span> Planned</span>
                  <span className={styles.legendItem}><span className={`${styles.legendDot} ${styles.legendCompleted}`}></span> Completed</span>
                </div>
              </div>
              <div className={styles.kpiCard}>
                <h2 className={styles.kpiTitle}>📈 Key Metrics</h2>
                <div className={styles.kpiRow}>
                  <div className={styles.kpiItem}>
                    <span className={styles.kpiValue}>{events.length}</span>
                    <span className={styles.kpiLabel}>📅 Total Events</span>
                  </div>
                  <div className={styles.kpiItem}>
                    <span className={styles.kpiValue}>{upcomingEvents.length}</span>
                    <span className={styles.kpiLabel}>⏭️ Upcoming</span>
                  </div>
                  <div className={styles.kpiItem}>
                    <span className={styles.kpiValue}>{eventsThisWeek}</span>
                    <span className={styles.kpiLabel}>📆 This Week</span>
                  </div>
                  <div className={styles.kpiItem}>
                    <span className={styles.kpiValue}>{avgAttendance}</span>
                    <span className={styles.kpiLabel}>👥 Avg Attendance</span>
                  </div>
                  <div className={styles.kpiItem}>
                    <span className={styles.kpiValue}>{completionRate}%</span>
                    <span className={styles.kpiLabel}>✅ Completion Rate</span>
                  </div>
                  <div className={styles.kpiItem}>
                    <span className={styles.kpiValue}>{uniqueLocations.length}</span>
                    <span className={styles.kpiLabel}>📍 Venues</span>
                  </div>
                </div>
              </div>
              <div className={styles.locationsCard}>
                <h2 className={styles.kpiTitle}>📍 Event Venues</h2>
                <div className={styles.locationsList}>
                  {uniqueLocations.length === 0 ? (
                    <div className={styles.emptyLocations}>
                      <span className={styles.emptyIcon}>🗺️</span>
                      <p>No venues yet</p>
                    </div>
                  ) : (
                    uniqueLocations.map(loc => (
                      <a
                        key={loc}
                        className={styles.locationItem}
                        href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(loc)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={`Open ${loc} in Google Maps`}
                      >
                        <FaMapMarkerAlt className={styles.locationIcon} />
                        <span className={styles.locationName}>{loc}</span>
                        <span className={styles.openMapIcon}>🔗</span>
                      </a>
                    ))
                  )}
                </div>
              </div>
            </div>
            </div>
          </div>
        )}
        
        {sortedFilteredEvents.length === 0 ? (
          <div className={styles.noEvents}>
            <div className={styles.noEventsIcon}>📅</div>
            <h3 className={styles.noEventsTitle}>
              No {activeTab} events {statusFilter !== 'all' ? `with status "${statusFilter}"` : ''}
            </h3>
            <p className={styles.noEventsText}>
              {activeTab === 'upcoming' 
                ? 'Check back later for new events or create one using the + button below.' 
                : 'Past events will appear here once they have occurred.'}
            </p>
          </div>
        ) : (
          <div className={styles.weeklyEventsContainer}>
            {weeklyGroupedEvents.map(week => (
              <div 
                key={week.weekKey} 
                className={styles.weekSection}
                data-week-key={week.weekKey}
              >
                <button 
                  className={styles.weekHeader}
                  onClick={() => toggleWeek(week.weekKey)}
                  aria-expanded={expandedWeeks[week.weekKey]}
                >
                  <span className={styles.weekArrow}>
                    {expandedWeeks[week.weekKey] ? '▼' : '▶'}
                  </span>
                  <span className={styles.weekLabel}>{week.label}</span>
                  <span className={styles.weekCount}>({week.events.length})</span>
                </button>
                
                {expandedWeeks[week.weekKey] && (
                  <div className={styles.weekEventsGrid}>
                    {week.events.map(event => (
                      <EventCard
                        key={event._id}
                        event={event}
                        isLoaded={isLoaded}
                        weatherData={weatherData}
                        onEditEvent={canManage ? openModal : null}
                        isHighlighted={highlightEventId === event._id}
                        canManage={canManage}
                        programId={programId}
                        programDetails={programDetails}
                        onEventUpdated={(updatedEvent) => {
                          // Lightweight refresh - no weather fetch
                          refreshEventsOnly();
                        }}
                      />
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        
        {/* Add Event Button - Only visible to managers */}
        {canManage && (
          <button 
            onClick={() => openModal()} 
            className={styles.addEventButton} 
            title="Add New Event" 
            aria-label="Add New Event"
          >
            +
          </button>
        )}

        
        {/* Modal for adding/editing event */}
        {isModalOpen && (
          <EventModal
            onClose={closeModal}
            programId={programId}
            clubId={resolvedClubId}
            eventToEdit={selectedEvent}
          />
        )}
      </div>
    </div>
  );
};

export default EventsPage;